import { useState } from "react";
import { PackagePlus } from "lucide-react";
import { validateParameterEntryValue } from "@opus-dt/shared-types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/notify";
import { getFriendlyErrorMessage } from "@/lib/api-client";
import type { ParameterDetail } from "@/types/line-editor.types";
import { useRegisterProductionEntry } from "./hooks/useRegisterProductionEntry";

interface Props {
  parameter: ParameterDetail;
  machineId: string;
  lineId: string;
}

// Reporte de produção por máquina. Mostra o acumulado da máquina na
// run (currentValue) e um incremento "+N produzido"; o rollup soma ao produzido da linha.
export function ProductionEntryBlock({ parameter, machineId, lineId }: Props) {
  const register = useRegisterProductionEntry(machineId, lineId);
  const [raw, setRaw] = useState("");
  const [error, setError] = useState<string | null>(null);
  const empty = raw.trim() === "";

  async function handleSubmit() {
    if (empty || register.isPending) return;
    const result = validateParameterEntryValue("COUNTER", raw);
    if (!result.ok) {
      setError(result.message);
      return;
    }
    setError(null);
    try {
      await register.mutateAsync({ parameterId: parameter.id, value: result.value });
      toast.success(`+${result.value} ${parameter.unit ?? ""} reportado.`.trim());
      setRaw("");
    } catch (err) {
      const msg = getFriendlyErrorMessage(err);
      toast.error("Falha ao reportar produção.", { description: msg });
    }
  }

  const fieldId = `prod-${parameter.id}`;
  return (
    <section className="space-y-2 rounded-md border p-3">
      <header className="space-y-1">
        <h3 className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-muted-foreground">
          <PackagePlus className="h-3.5 w-3.5" />
          Reportar produção — {parameter.name}
        </h3>
        <p className="text-xs text-muted-foreground">
          Acumulado nesta ordem:{" "}
          <span className="font-mono font-semibold text-foreground">
            {parameter.currentValue ?? "0"}
          </span>{" "}
          {parameter.unit ?? ""}
        </p>
      </header>

      <form
        className="space-y-1"
        onSubmit={(e) => {
          e.preventDefault();
          void handleSubmit();
        }}
      >
        <Label htmlFor={fieldId} className="text-[11px]">
          Incremento produzido
          {parameter.unit ? (
            <span className="font-normal text-muted-foreground"> ({parameter.unit})</span>
          ) : null}
        </Label>
        <div className="flex items-center gap-2">
          <Input
            id={fieldId}
            value={raw}
            inputMode="numeric"
            placeholder="0"
            className="h-8 flex-1"
            onChange={(e) => {
              setRaw(e.target.value);
              if (error) setError(null);
            }}
          />
          <Button type="submit" size="sm" disabled={empty || register.isPending}>
            {register.isPending ? "…" : "Registrar"}
          </Button>
        </div>
        {error ? <p className="text-xs text-rd-danger-700">{error}</p> : null}
      </form>
    </section>
  );
}
